import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Loader2,
  Upload,
  Users,
} from 'lucide-react';
import { toast } from '@clareefai/ui';
import type { RecipientSource } from '@clareefai/types';
import { EmailTemplateType } from '../../store/emailTemplateApi';
import { useGetSurveyQuery, useGetHubSpotContactListsQuery, useGetSalesforceCampaignsQuery, useSendSurveyMutation } from '../../store/surveyApi';
import { useGetPromotersQuery, useGetImportedContactsQuery } from '../../store/promoterApi';
import { useAuth } from '../../contexts/AuthContext';
import EmailPreviewCard from '../../components/shared/EmailPreviewCard';

interface CsvRow {
  name: string;
  email: string;
}

const STEP_KEYS = ['surveys.send.steps.recipients', 'surveys.send.steps.customizeEmail', 'surveys.send.steps.previewSend'];

const StepIndicator: React.FC<{ currentStep: number }> = ({ currentStep }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 mb-8">
      {STEP_KEYS.map((key, i) => (
        <React.Fragment key={key}>
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                i < currentStep
                  ? 'bg-purple-600 text-white'
                  : i === currentStep
                    ? 'bg-purple-600 text-white ring-4 ring-purple-100'
                    : 'bg-gray-200 text-gray-500'
              }`}
            >
              {i < currentStep ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
            </div>
            <span
              className={`text-sm font-medium hidden sm:block ${
                i === currentStep ? 'text-gray-900' : 'text-gray-400'
              }`}
            >
              {t(key)}
            </span>
          </div>
          {i < STEP_KEYS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 ${i < currentStep ? 'bg-purple-600' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const SendSurvey: React.FC = () => {
  const { t } = useTranslation();
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();

  const { firebaseUser } = useAuth();
  const { data: survey } = useGetSurveyQuery(surveyId!, { skip: !surveyId });
  const { data: promoters = [], isLoading: promotersLoading } = useGetPromotersQuery();
  const { data: importedContacts = [] } = useGetImportedContactsQuery();
  const { data: hubspotLists = [] } = useGetHubSpotContactListsQuery();
  const { data: salesforceCampaigns = [] } = useGetSalesforceCampaignsQuery();
  const [sendSurvey, { isLoading: isSending }] = useSendSurveyMutation();

  const [step, setStep] = useState(0);
  const [recipientSource, setRecipientSource] = useState<RecipientSource>('ALL_PROMOTERS');
  const [selectedPromoterIds, setSelectedPromoterIds] = useState<Set<string>>(new Set());
  const [selectedHubspotListIds, setSelectedHubspotListIds] = useState<Set<string>>(new Set());
  const [selectedSalesforceCampaignIds, setSelectedSalesforceCampaignIds] = useState<Set<string>>(new Set());
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
  const [csvFileName, setCsvFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [individualFirstName, setIndividualFirstName] = useState('');
  const [individualLastName, setIndividualLastName] = useState('');
  const [individualEmail, setIndividualEmail] = useState('');
  const [individualCompany, setIndividualCompany] = useState('');

  const togglePromoter = (id: string) =>
    setSelectedPromoterIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectAllPromoters = () =>
    setSelectedPromoterIds(
      selectedPromoterIds.size === promoters.length ? new Set() : new Set(promoters.map((p) => p.id))
    );

  const toggleHubspotList = (listId: string) =>
    setSelectedHubspotListIds((prev) => {
      const next = new Set(prev);
      next.has(listId) ? next.delete(listId) : next.add(listId);
      return next;
    });

  const toggleSalesforceCampaign = (campaignId: string) =>
    setSelectedSalesforceCampaignIds((prev) => {
      const next = new Set(prev);
      next.has(campaignId) ? next.delete(campaignId) : next.add(campaignId);
      return next;
    });

  const hubspotTotalContacts = [...selectedHubspotListIds].reduce((sum, listId) => {
    return sum + (hubspotLists.find((l) => l.listId === listId)?.contactCount ?? 0);
  }, 0);

  const salesforceTotalContacts = [...selectedSalesforceCampaignIds].reduce((sum, campaignId) => {
    return sum + (salesforceCampaigns.find((c) => c.campaignId === campaignId)?.memberCount ?? 0);
  }, 0);

  const crmTotalContacts = hubspotTotalContacts + salesforceTotalContacts;
  const crmSelectionCount = selectedHubspotListIds.size + selectedSalesforceCampaignIds.size;

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length < 2) { toast.error(t('surveys.send.csvMissingHeader')); return; }
      const header = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/"/g, ''));
      const emailIdx = header.findIndex((h) => h === 'email');
      const nameIdx = header.findIndex((h) => ['name', 'full_name', 'fullname'].includes(h));
      const firstIdx = header.findIndex((h) => ['first_name', 'firstname'].includes(h));
      const lastIdx = header.findIndex((h) => ['last_name', 'lastname'].includes(h));
      if (emailIdx === -1) { toast.error(t('surveys.send.csvMissingEmail')); return; }
      const rows: CsvRow[] = lines.slice(1).map((line) => {
        const cols = line.split(',').map((c) => c.trim().replace(/"/g, ''));
        const email = cols[emailIdx] ?? '';
        let name = '';
        if (nameIdx >= 0) name = cols[nameIdx] ?? '';
        else if (firstIdx >= 0) name = `${cols[firstIdx] ?? ''} ${cols[lastIdx] ?? ''}`.trim();
        else name = email.split('@')[0];
        return { name, email };
      }).filter((r) => r.email);
      setCsvRows(rows);
      toast.success(t('surveys.send.csvContactsLoaded', { count: rows.length }));
    };
    reader.readAsText(file);
  };

  const recipientCount =
    recipientSource === 'ALL_PROMOTERS' ? promoters.length
    : recipientSource === 'SELECTED_PROMOTERS' ? selectedPromoterIds.size
    : recipientSource === 'CRM_IMPORT' ? crmTotalContacts
    : recipientSource === 'RECENTLY_IMPORTED' ? importedContacts.length
    : recipientSource === 'INDIVIDUAL' ? (individualEmail.trim() ? 1 : 0)
    : csvRows.length;

  const canContinueStep0 = () => {
    if (recipientSource === 'ALL_PROMOTERS') return promoters.length > 0;
    if (recipientSource === 'SELECTED_PROMOTERS') return selectedPromoterIds.size > 0;
    if (recipientSource === 'CRM_IMPORT') return crmSelectionCount > 0;
    if (recipientSource === 'RECENTLY_IMPORTED') return importedContacts.length > 0;
    if (recipientSource === 'CSV_IMPORT') return csvRows.length > 0;
    if (recipientSource === 'INDIVIDUAL') return individualEmail.trim().length > 0 && individualFirstName.trim().length > 0;
    return false;
  };

  const handleSend = async () => {
    if (!surveyId) return;
    try {
      let result;
      if (recipientSource === 'CSV_IMPORT') {
        const formData = new FormData();
        const blob = new Blob(
          [`name,email\n${csvRows.map((r) => `"${r.name}","${r.email}"`).join('\n')}`],
          { type: 'text/csv' }
        );
        formData.append('file', blob, 'recipients.csv');
        const apiBase = import.meta.env.VITE_API_BASE_URL ?? '/api';
        const token = await firebaseUser?.getIdToken();
        const res = await fetch(`${apiBase}/vendors/me/surveys/${surveyId}/send-csv`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        result = await res.json();
      } else {
        result = await sendSurvey({
          surveyId,
          data: {
            recipientSource,
            promoterIds: recipientSource === 'SELECTED_PROMOTERS' ? Array.from(selectedPromoterIds) : undefined,
            hubspotListIds: recipientSource === 'CRM_IMPORT' && selectedHubspotListIds.size > 0 ? Array.from(selectedHubspotListIds) : undefined,
            salesforceCampaignIds: recipientSource === 'CRM_IMPORT' && selectedSalesforceCampaignIds.size > 0 ? Array.from(selectedSalesforceCampaignIds) : undefined,
            individualFirstName: recipientSource === 'INDIVIDUAL' ? individualFirstName.trim() : undefined,
            individualLastName: recipientSource === 'INDIVIDUAL' ? individualLastName.trim() : undefined,
            individualEmail: recipientSource === 'INDIVIDUAL' ? individualEmail.trim() : undefined,
            individualCompany: recipientSource === 'INDIVIDUAL' ? individualCompany.trim() || undefined : undefined,
          },
        }).unwrap();
      }
      toast.success(t('surveys.send.surveySentSuccess', { count: result.invitationsSent ?? recipientCount }));
      navigate(`/survey/${surveyId}`);
    } catch {
      toast.error(t('surveys.send.failedToSend'));
    }
  };

  const sourceOptions: Array<{
    value: RecipientSource;
    label: string;
    description: string;
    badge?: string;
    count?: number;
  }> = [
    { value: 'ALL_PROMOTERS', label: t('surveys.send.allPromoters'), description: t('surveys.send.allPromotersDesc'), count: promoters.length },
    { value: 'SELECTED_PROMOTERS', label: t('surveys.send.selectPromoters'), description: t('surveys.send.selectPromotersDesc') },
    { value: 'CRM_IMPORT', label: t('surveys.send.crmImport'), description: t('surveys.send.crmImportDesc') },
    { value: 'CSV_IMPORT', label: t('surveys.send.csvImport'), description: t('surveys.send.csvImportDesc') },
    { value: 'RECENTLY_IMPORTED', label: t('surveys.send.recentlyImported'), description: t('surveys.send.recentlyImportedDesc'), badge: t('surveys.send.badgeNew'), count: importedContacts.length },
    { value: 'INDIVIDUAL', label: t('surveys.send.individual'), description: t('surveys.send.individualDesc') },
  ];

  return (
    <div className="flex justify-center">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 w-full max-w-2xl shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(`/survey/${surveyId}`)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('surveys.send.title')}</h1>
            <p className="text-sm text-gray-500">
              {survey ? t('surveys.send.sending', { title: survey.title }) : t('surveys.send.defaultSubtitle')}
            </p>
          </div>
        </div>

        <StepIndicator currentStep={step} />

        {/* Step 0: Select recipients */}
        {step === 0 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-semibold text-gray-900 mb-0.5">{t('surveys.send.selectRecipients')}</h2>
              <p className="text-sm text-gray-500">{t('surveys.send.selectRecipientsHelp')}</p>
            </div>

            <div className="space-y-2.5">
              {sourceOptions.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                    recipientSource === opt.value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="recipientSource"
                    value={opt.value}
                    checked={recipientSource === opt.value}
                    onChange={() => setRecipientSource(opt.value)}
                    className="mt-0.5 accent-purple-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                      {opt.badge && (
                        <span className="text-xs font-bold bg-orange-100 text-orange-600 rounded-full px-1.5 py-0.5">
                          {opt.badge}
                        </span>
                      )}
                      {opt.count !== undefined && (
                        <span className="ml-auto text-sm font-semibold text-purple-600">{t('surveys.send.recipientsCount', { count: opt.count })}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>

                    {/* CRM multi-list selector (HubSpot lists + Salesforce campaigns) */}
                    {opt.value === 'CRM_IMPORT' && recipientSource === 'CRM_IMPORT' && (
                      <div className="mt-3 space-y-3">
                        {hubspotLists.length === 0 && salesforceCampaigns.length === 0 ? (
                          <p className="text-xs text-amber-600 bg-amber-50 rounded-lg p-2">
                            {t('surveys.send.noCrmConnection')}
                          </p>
                        ) : (
                          <>
                            {hubspotLists.length > 0 && (
                              <>
                                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-2 text-sm text-green-700">
                                  <CheckCircle2 className="w-4 h-4" />
                                  {t('surveys.send.connectedToHubSpot')}
                                </div>
                                <p className="text-xs font-medium text-gray-600">{t('surveys.send.selectLists')}</p>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  {hubspotLists.map((list) => (
                                    <label
                                      key={list.listId}
                                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                                        selectedHubspotListIds.has(list.listId)
                                          ? 'border-purple-300 bg-purple-50'
                                          : 'border-gray-100 hover:border-gray-200'
                                      }`}
                                    >
                                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                        selectedHubspotListIds.has(list.listId)
                                          ? 'bg-purple-600 border-purple-600'
                                          : 'border-gray-300'
                                      }`}>
                                        {selectedHubspotListIds.has(list.listId) && (
                                          <Check className="w-2.5 h-2.5 text-white" />
                                        )}
                                      </div>
                                      <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={selectedHubspotListIds.has(list.listId)}
                                        onChange={() => toggleHubspotList(list.listId)}
                                      />
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-800">{list.name}</p>
                                        <p className="text-xs text-gray-400">{t('surveys.send.contacts', { count: list.contactCount })}</p>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              </>
                            )}
                            {salesforceCampaigns.length > 0 && (
                              <>
                                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-2 text-sm text-green-700">
                                  <CheckCircle2 className="w-4 h-4" />
                                  {t('surveys.send.connectedToSalesforce')}
                                </div>
                                <p className="text-xs font-medium text-gray-600">{t('surveys.send.selectCampaigns')}</p>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  {salesforceCampaigns.map((campaign) => (
                                    <label
                                      key={campaign.campaignId}
                                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                                        selectedSalesforceCampaignIds.has(campaign.campaignId)
                                          ? 'border-purple-300 bg-purple-50'
                                          : 'border-gray-100 hover:border-gray-200'
                                      }`}
                                    >
                                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                        selectedSalesforceCampaignIds.has(campaign.campaignId)
                                          ? 'bg-purple-600 border-purple-600'
                                          : 'border-gray-300'
                                      }`}>
                                        {selectedSalesforceCampaignIds.has(campaign.campaignId) && (
                                          <Check className="w-2.5 h-2.5 text-white" />
                                        )}
                                      </div>
                                      <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={selectedSalesforceCampaignIds.has(campaign.campaignId)}
                                        onChange={() => toggleSalesforceCampaign(campaign.campaignId)}
                                      />
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-800">{campaign.name}</p>
                                        <p className="text-xs text-gray-400">{t('surveys.send.contacts', { count: campaign.memberCount })}</p>
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              </>
                            )}
                            {crmSelectionCount > 0 && (
                              <div className="flex items-center justify-between text-sm pt-1">
                                <span className="text-gray-500">{t('surveys.send.totalContactsToImport')}</span>
                                <span className="font-bold text-purple-600">{t('surveys.send.totalContacts', { count: crmTotalContacts })}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* CSV upload */}
                    {opt.value === 'CSV_IMPORT' && recipientSource === 'CSV_IMPORT' && (
                      <div className="mt-3">
                        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvUpload} className="hidden" />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-purple-300 hover:text-purple-600 transition-colors w-full justify-center"
                        >
                          <Upload className="w-4 h-4" />
                          {csvFileName || t('surveys.send.chooseCsvFile')}
                        </button>
                        {csvRows.length > 0 && (
                          <p className="text-xs text-green-600 mt-1.5 font-medium">{t('surveys.send.contactsLoaded', { count: csvRows.length })}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{t('surveys.send.csvFormatHelp')}</p>
                      </div>
                    )}

                    {/* Individual form */}
                    {opt.value === 'INDIVIDUAL' && recipientSource === 'INDIVIDUAL' && (
                      <div className="mt-3 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('surveys.send.firstName')} *</label>
                            <input
                              type="text"
                              value={individualFirstName}
                              onChange={(e) => setIndividualFirstName(e.target.value)}
                              placeholder="John"
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">{t('surveys.send.lastName')} *</label>
                            <input
                              type="text"
                              value={individualLastName}
                              onChange={(e) => setIndividualLastName(e.target.value)}
                              placeholder="Doe"
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('surveys.send.email')} *</label>
                          <input
                            type="email"
                            value={individualEmail}
                            onChange={(e) => setIndividualEmail(e.target.value)}
                            placeholder="john.doe@company.com"
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('surveys.send.company')} <span className="text-gray-400 font-normal">{t('surveys.send.companyOptional')}</span></label>
                          <input
                            type="text"
                            value={individualCompany}
                            onChange={(e) => setIndividualCompany(e.target.value)}
                            placeholder="Acme Inc."
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>

            {/* Promoter grid for SELECTED_PROMOTERS */}
            {recipientSource === 'SELECTED_PROMOTERS' && (
              <div className="space-y-3 mt-2 border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">
                    {t('surveys.send.selectedOfTotal', { selected: selectedPromoterIds.size, total: promoters.length })}
                  </p>
                  <button onClick={selectAllPromoters} className="text-xs text-purple-600 hover:underline">
                    {selectedPromoterIds.size === promoters.length ? t('surveys.send.deselectAll') : t('surveys.send.selectAll', { count: promoters.length })}
                  </button>
                </div>
                {promotersLoading ? (
                  <div className="grid grid-cols-2 gap-2">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-2 pr-1">
                    {promoters.map((promoter) => (
                      <label
                        key={promoter.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedPromoterIds.has(promoter.id)
                            ? 'border-purple-400 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedPromoterIds.has(promoter.id)
                            ? 'bg-purple-600 border-purple-600'
                            : 'border-gray-300'
                        }`}>
                          {selectedPromoterIds.has(promoter.id) && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={selectedPromoterIds.has(promoter.id)}
                          onChange={() => togglePromoter(promoter.id)}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-gray-900 truncate">
                            {promoter.firstName} {promoter.lastName}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{promoter.email}</p>
                          {promoter.company && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Building2 className="w-3 h-3 text-gray-300" />
                              <p className="text-xs text-gray-400 truncate">{promoter.company}</p>
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recently imported contacts list */}
            {recipientSource === 'RECENTLY_IMPORTED' && importedContacts.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-4 space-y-2">
                <p className="text-xs font-medium text-gray-600">{t('surveys.send.importedContactsHelp', { count: importedContacts.length })}</p>
                <div className="max-h-48 overflow-y-auto space-y-1.5">
                  {importedContacts.slice(0, 20).map((contact) => (
                    <div key={contact.id} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-purple-600">
                          {contact.firstName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">{contact.firstName} {contact.lastName}</p>
                        <p className="text-xs text-gray-400 truncate">{contact.email}</p>
                      </div>
                      {contact.company && (
                        <span className="ml-auto text-xs text-gray-400 truncate max-w-20">{contact.company}</span>
                      )}
                    </div>
                  ))}
                  {importedContacts.length > 20 && (
                    <p className="text-xs text-gray-400 text-center py-1">{t('surveys.send.moreItems', { count: importedContacts.length - 20 })}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Email template preview */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-900 mb-0.5">{t('surveys.send.emailTemplate')}</h2>
                <p className="text-sm text-gray-500">{t('surveys.send.previewEmailDesc')}</p>
              </div>
              <a
                href="/settings/email-templates"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                {t('surveys.send.customizeInSettings')}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <EmailPreviewCard templateType={EmailTemplateType.SURVEY_INVITATION} />
          </div>
        )}

        {/* Step 2: Preview & send */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <h2 className="font-semibold text-gray-900 mb-0.5">{t('surveys.send.previewAndSend')}</h2>
              <p className="text-sm text-gray-500">{t('surveys.send.reviewBeforeSending')}</p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-4 bg-purple-50 border border-purple-100 rounded-xl">
                <Users className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {recipientCount === 1 ? t('surveys.send.recipientCountSingle', { count: recipientCount }) : t('surveys.send.recipientCountMultiple', { count: recipientCount })}
                  </p>
                  <p className="text-xs text-gray-500">
                    {recipientSource === 'ALL_PROMOTERS' && t('surveys.send.summaryAllPromoters')}
                    {recipientSource === 'SELECTED_PROMOTERS' && t('surveys.send.summarySelectedPromoters', { count: selectedPromoterIds.size })}
                    {recipientSource === 'CRM_IMPORT' && t('surveys.send.summaryCrm', { count: crmSelectionCount })}
                    {recipientSource === 'CSV_IMPORT' && t('surveys.send.summaryCsv')}
                    {recipientSource === 'RECENTLY_IMPORTED' && t('surveys.send.summaryRecent')}
                    {recipientSource === 'INDIVIDUAL' && t('surveys.send.summaryIndividual', { firstName: individualFirstName, lastName: individualLastName, email: individualEmail })}
                  </p>
                </div>
              </div>
              <div className="p-4 border border-gray-200 rounded-xl">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-3">{t('surveys.send.emailTemplateLabel')}</p>
                <p className="text-sm text-gray-600" dangerouslySetInnerHTML={{ __html: t('surveys.send.usingTemplate') }} />
              </div>
              <p className="text-xs text-gray-400">{t('surveys.send.uniqueLinkHelp')}</p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
          <div>
            {step > 0 ? (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('surveys.send.back')}
              </button>
            ) : (
              <button onClick={() => navigate(`/survey/${surveyId}`)} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2.5">
                {t('common.cancel')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">{t('surveys.send.stepOf', { current: step + 1, total: STEP_KEYS.length })}</span>
            {step < STEP_KEYS.length - 1 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={step === 0 && !canContinueStep0()}
                className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('surveys.send.continue')}
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={isSending}
                className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {isSending && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSending ? t('surveys.send.sendingButton') : t('surveys.send.sendButton')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendSurvey;
